import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { PaginatedResponse } from '@app/core/models/shared/admin.model';
import { toLocalDateTimePeriod } from '@app/core/utils/local-date-time';
import {
  MarketingPeriod,
  PromoCode,
  PromoCodePurchase,
  PromoCodePurchaseStatus,
  PromoCodeRefund,
  PromoCodeSummary,
  ReferralClick,
  ReferralClickStatus,
  ReferralLink,
  ReferralPurchase,
  ReferralRefund,
  ReferralSummary,
  RewardSummary,
} from '@app/core/models/partner/partner-marketing.model';
import { MarketingApi } from './marketing-api.token';

type QueryValue = string | number | null | undefined;

/** Swagger: PromoPurchase — агентский API отдает camelCase */
interface AgentPromoPurchase {
  certificateSaleId: string;
  promoId: string;
  code: string;
  paidAt: string | null;
  discountPercent: number | null;
  discountAmount: number | null;
  paidAmount: number | null;
  currency: string;
  channel: string;
  certificateStatus: string | null;
  refundedAmount: number | null;
  rewardPercent: number | null;
}

/** Swagger: PromoRefund */
interface AgentPromoRefund {
  id: string;
  certificateSaleId: string;
  promoId: string;
  code: string;
  refundedAt: string;
  amount: number;
  currency: string;
}

function emptyPage<T>(): PaginatedResponse<T> {
  return {
    currentPageNumber: 0,
    pageSize: 0,
    totalElements: 0,
    totalPages: 0,
    isLastPage: true,
    isFirstPage: true,
    isEmpty: true,
    content: [],
  };
}

/**
 * Кабинет текущего агента: /api/b2b/agent/marketing/*.
 * Сигнатуры совпадают с PartnerMarketingService (partnerId игнорируется — бэкенд берет агента из токена),
 * ответы приводятся к тем же моделям, что и в админке.
 */
@Injectable({
  providedIn: 'root',
})
export class AgentMarketingService implements MarketingApi {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/agent/marketing`;
  }

  // ---------- Referral ----------

  /**
   * Существующая ссылка текущего агента — GET, ничего не создает.
   * 4xx (404 «ссылки нет» и т.п.) — ссылки еще нет: null без тоста, в кабинете появится «Получить ссылку».
   */
  getReferralLink(_partnerId?: string): Observable<ReferralLink | null> {
    return this.http.get<ReferralLink>(`${this.baseUrl}/link`).pipe(
      catchError(error =>
        error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500
          ? of(null)
          : this.fallback(error, 'Не удалось получить реферальную ссылку', null)
      )
    );
  }

  /** Получить существующую или создать постоянную рекламную ссылку и код текущего агента — PUT */
  createReferralLink(_partnerId?: string): Observable<ReferralLink | null> {
    return this.http
      .put<ReferralLink>(`${this.baseUrl}/link`, {})
      .pipe(catchError(error => this.fallback(error, 'Не удалось получить реферальную ссылку', null)));
  }

  getReferralSummary(_partnerId: string, period: MarketingPeriod): Observable<ReferralSummary | null> {
    return this.http
      .get<ReferralSummary>(`${this.baseUrl}/referrals/summary`, { params: this.params({ ...period }) })
      .pipe(catchError(error => this.fallback(error, 'Не удалось загрузить реферальную статистику', null)));
  }

  getReferralClicks(
    _partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod,
    status?: ReferralClickStatus | null
  ): Observable<PaginatedResponse<ReferralClick>> {
    return this.http
      .get<PaginatedResponse<ReferralClick>>(`${this.baseUrl}/referrals/clicks`, {
        params: this.params({ ...period, status, page, size }),
      })
      .pipe(
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить переходы по ссылке', emptyPage<ReferralClick>())
        )
      );
  }

  getReferralPurchases(
    _partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod
  ): Observable<PaginatedResponse<ReferralPurchase>> {
    return this.http
      .get<PaginatedResponse<ReferralPurchase>>(`${this.baseUrl}/referrals/purchases`, {
        params: this.params({ ...period, page, size }),
      })
      .pipe(
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить реферальные покупки', emptyPage<ReferralPurchase>())
        )
      );
  }

  getReferralPurchase(_partnerId: string, purchaseId: string): Observable<ReferralPurchase | null> {
    return this.http
      .get<ReferralPurchase>(`${this.baseUrl}/referrals/purchases/${purchaseId}`)
      .pipe(catchError(error => this.fallback(error, 'Не удалось загрузить покупку', null)));
  }

  getReferralRefunds(
    _partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod
  ): Observable<PaginatedResponse<ReferralRefund>> {
    return this.http
      .get<PaginatedResponse<ReferralRefund>>(`${this.baseUrl}/referrals/refunds`, {
        params: this.params({ ...period, page, size }),
      })
      .pipe(
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить возвраты', emptyPage<ReferralRefund>())
        )
      );
  }

  // ---------- Promo codes ----------

  getPromoCodes(_partnerId: string, page: number, size: number): Observable<PaginatedResponse<PromoCode>> {
    return this.http
      .get<PaginatedResponse<PromoCode>>(`${this.baseUrl}/promocodes`, { params: this.params({ page, size }) })
      .pipe(
        catchError(error => this.fallback(error, 'Не удалось загрузить промокоды', emptyPage<PromoCode>()))
      );
  }

  getPromoCodeSummary(
    _partnerId: string,
    period: MarketingPeriod,
    code?: string | null
  ): Observable<PromoCodeSummary | null> {
    return this.http
      .get<PromoCodeSummary>(`${this.baseUrl}/promocodes/summary`, {
        params: this.params({ ...toLocalDateTimePeriod(period), code }),
      })
      .pipe(catchError(error => this.fallback(error, 'Не удалось загрузить статистику промокодов', null)));
  }

  getPromoCodePurchases(
    _partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod,
    code?: string | null,
    status?: PromoCodePurchaseStatus | null
  ): Observable<PaginatedResponse<PromoCodePurchase>> {
    return this.http
      .get<PaginatedResponse<AgentPromoPurchase>>(`${this.baseUrl}/promocodes/purchases`, {
        params: this.params({ ...toLocalDateTimePeriod(period), code, status, page, size }),
      })
      .pipe(
        map(res => ({ ...res, content: (res.content ?? []).map(toAdminPromoPurchase) })),
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить покупки по промокодам', emptyPage<PromoCodePurchase>())
        )
      );
  }

  getPromoCodeRefunds(
    _partnerId: string,
    page: number,
    size: number,
    period: MarketingPeriod,
    code?: string | null
  ): Observable<PaginatedResponse<PromoCodeRefund>> {
    return this.http
      .get<PaginatedResponse<AgentPromoRefund>>(`${this.baseUrl}/promocodes/refunds`, {
        params: this.params({ ...toLocalDateTimePeriod(period), code, page, size }),
      })
      .pipe(
        map(res => ({ ...res, content: (res.content ?? []).map(toAdminPromoRefund) })),
        catchError(error =>
          this.fallback(error, 'Не удалось загрузить возвраты по промокодам', emptyPage<PromoCodeRefund>())
        )
      );
  }

  // ---------- Rewards ----------

  getRewardSummary(_partnerId: string, period: MarketingPeriod): Observable<RewardSummary | null> {
    return this.http
      .get<RewardSummary>(`${this.baseUrl}/rewards/summary`, { params: this.params({ ...period }) })
      .pipe(catchError(error => this.fallback(error, 'Не удалось загрузить расчет вознаграждения', null)));
  }

  // ---------- helpers ----------

  private params(values: Record<string, QueryValue>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(values)) {
      if (value === null || value === undefined || value === '') continue;
      params = params.set(key, String(value));
    }
    return params;
  }

  private fallback<T>(error: unknown, messageKey: string, value: T): Observable<T> {
    console.error(`${messageKey}:`, error);
    this.translate.get(messageKey).subscribe(msg => this.snackbarService.error(msg));
    return of(value);
  }
}

/** camelCase-строка агента -> snake_case-модель, которую отображает таблица промокодов */
function toAdminPromoPurchase(row: AgentPromoPurchase): PromoCodePurchase {
  return {
    certificate_sale_id: row.certificateSaleId,
    promo_id: row.promoId,
    user_id: '',
    partner_id: '',
    partner_name: null,
    code: row.code,
    discount_percent: row.discountPercent ?? 0,
    discount_amount: row.discountAmount ?? 0,
    base_price: (row.paidAmount ?? 0) + (row.discountAmount ?? 0),
    final_price: row.paidAmount ?? 0,
    paid_amount: row.paidAmount ?? 0,
    currency: row.currency,
    channel: row.channel as PromoCodePurchase['channel'],
    network_id: null,
    store_id: null,
    state: '',
    reserved_at: null,
    paid_at: row.paidAt,
    historical: false,
    reward_percent: row.rewardPercent,
    serial_number: null,
    certificate_status: row.certificateStatus,
    refund_amount: row.refundedAmount,
  };
}

function toAdminPromoRefund(row: AgentPromoRefund): PromoCodeRefund {
  return {
    id: row.id,
    certificate_sale_id: row.certificateSaleId,
    promo_id: row.promoId,
    code: row.code,
    refunded_at: row.refundedAt,
    amount: row.amount,
    currency: row.currency,
  };
}
